import { createTheme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import '@mui/x-data-grid/themeAugmentation';

// Extend the MUI theme to include custom palette colors
declare module '@mui/material/styles/createPalette' {
  interface Palette {
    inherited: {
      main: string;
      dark: string;
      light: string;
      contrastText: string;
    };
  }
  interface PaletteOptions {
    inherited?: {
      main: string;
      dark: string;
      light: string;
      contrastText: string;
    };
  }
}

// System font stack as fallback for offline mode
const systemFontStack =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';

// Complete font family with Roboto and fallbacks
const fontFamily = `"Roboto", ${systemFontStack}`;

const baseTheme: ThemeOptions = {
  shape: {
    borderRadius: 8
  },
  typography: {
    fontFamily,
    h1: {
      fontFamily,
      fontSize: '96px',
      fontWeight: 300,
      lineHeight: 1.167,
      letterSpacing: '-1.5px'
    },
    h2: {
      fontFamily,
      fontSize: '60px',
      fontWeight: 300,
      lineHeight: 1.2,
      letterSpacing: '-0.5px'
    },
    h3: {
      fontFamily,
      fontSize: '48px',
      fontWeight: 400,
      lineHeight: 1.167,
      letterSpacing: '0px'
    },
    h4: {
      fontFamily,
      fontSize: '34px',
      fontWeight: 400,
      lineHeight: 1.235,
      letterSpacing: '0.25px'
    },
    h5: {
      fontFamily,
      fontSize: '24px',
      fontWeight: 400,
      lineHeight: 1.334,
      letterSpacing: '0px'
    },
    h6: {
      fontFamily,
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: 1.6,
      letterSpacing: '0.15px'
    },
    subtitle1: {
      fontFamily,
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.75,
      letterSpacing: '0.15px'
    },
    subtitle2: {
      fontFamily,
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.57,
      letterSpacing: '0.1px'
    },
    body1: {
      fontFamily,
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.15px'
    },
    body2: {
      fontFamily,
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: '0.17px'
    },
    button: {
      fontFamily,
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.75,
      letterSpacing: '0.4px',
      textTransform: 'uppercase'
    },
    caption: {
      fontFamily,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.4px'
    },
    overline: {
      fontFamily,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 2.66,
      letterSpacing: '1px',
      textTransform: 'uppercase'
    },
    allVariants: {
      fontFamily
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536
    }
  }
};

const lightTheme: ThemeOptions = {
  ...baseTheme,
  palette: {
    mode: 'light',
    primary: {
      main: 'rgba(1, 103, 124, 1)', // Vanguard teal
      dark: '#004E5A',
      light: '#449CB4',
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#6249C2',
      dark: '#4A2EA8',
      light: '#967EFA',
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#B91A1A',
      dark: '#930009',
      light: '#FF5349',
      contrastText: '#FFFFFF'
    },
    warning: {
      main: '#7E4C00',
      dark: '#613A00',
      light: '#C17400',
      contrastText: '#FFFFFF'
    },
    success: {
      main: '#006611',
      dark: '#005133',
      light: '#3CA36F',
      contrastText: '#FFFFFF'
    },
    info: {
      main: '#006DD1',
      dark: '#004E5E',
      light: '#449CB4',
      contrastText: '#FFFFFF'
    },
    background: {
      default: '#FFFFFF', // White background like Vanguard
      paper: '#FFFFFF'
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.60)'
    },
    action: {
      disabled: 'rgba(0, 0, 0, 0.38)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)',
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)'
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    inherited: {
      main: 'rgba(0, 0, 0, 0.87)',
      dark: 'rgba(0, 0, 0, 0.60)',
      light: 'rgba(0, 0, 0, 0.56)',
      contrastText: '#FFFFFF'
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '14px',
          letterSpacing: '0.4px'
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          }
        },
        containedPrimary: {
          backgroundColor: 'rgba(1, 103, 124, 1)',
          '&:hover': {
            backgroundColor: '#004E5A'
          },
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.38)',
            backgroundColor: 'rgba(0, 0, 0, 0.12)'
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            fontFamily
          },
          '& .MuiInputLabel-root': {
            fontFamily
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily,
          fontSize: '16px'
        },
        input: {
          fontFamily,
          fontSize: '16px',
          '&::placeholder': {
            fontFamily
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: 'rgba(0, 0, 0, 0.87)',
          boxShadow:
            '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
          '& *': {
            fontFamily
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          '& *': {
            fontFamily
          }
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '56px !important',
          '& *': {
            fontFamily
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily
        },
        label: {
          fontFamily
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily
        },
        secondary: {
          fontFamily
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '& *': {
            fontFamily
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily
        },
        message: {
          fontFamily
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily,
          fontSize: '14px'
        }
      }
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '*': {
            fontFamily
          },
          html: {
            fontFamily
          },
          body: {
            fontFamily
          }
        },
        body: {
          backgroundColor: '#FFFFFF',
          fontFamily,
          scrollbarColor: '#babac0 transparent',
          '&::-webkit-scrollbar': {
            backgroundColor: 'transparent',
            width: '6px',
            height: '6px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: '#f4f4f4'
            }
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#babac0',
            borderRadius: '16px',
            border: '1px solid transparent',
            '&:hover': {
              backgroundColor: '#a0a0a5'
            }
          },
          '&::-webkit-scrollbar-button': {
            display: 'none'
          }
        }
      }
    }
  }
};

const darkTheme: ThemeOptions = {
  ...baseTheme,
  palette: {
    mode: 'dark',
    primary: {
      main: '#4db8cc', // More vibrant teal for dark mode
      dark: '#004E5A',
      light: '#6dd3e0', // Better contrast
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#967EFA',
      dark: '#6249C2',
      light: '#B7A5FF',
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#FF5349',
      dark: '#B91A1A',
      light: '#FF7769',
      contrastText: '#FFFFFF'
    },
    warning: {
      main: '#C17400',
      dark: '#7E4C00',
      light: '#FF9500',
      contrastText: '#FFFFFF'
    },
    success: {
      main: '#3CA36F',
      dark: '#006611',
      light: '#5EBF8B',
      contrastText: '#FFFFFF'
    },
    info: {
      main: '#449CB4',
      dark: '#006DD1',
      light: '#66B2CC',
      contrastText: '#FFFFFF'
    },
    background: {
      default: '#121218', // Soft dark blue-gray
      paper: '#1e1e26' // Slightly elevated surface
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#3a3a44', // Updated for better contrast
      800: '#2a2a34', // Updated for better contrast
      900: '#1a1a24' // Updated for better contrast
    },
    text: {
      primary: '#e4e4e7', // Soft white to reduce eye strain
      secondary: 'rgba(228, 228, 231, 0.65)' // Softer secondary text
    },
    action: {
      disabled: 'rgba(228, 228, 231, 0.3)',
      disabledBackground: 'rgba(228, 228, 231, 0.08)',
      hover: 'rgba(228, 228, 231, 0.05)',
      selected: 'rgba(228, 228, 231, 0.12)'
    },
    divider: 'rgba(228, 228, 231, 0.08)',
    inherited: {
      main: 'rgba(228, 228, 231, 0.87)',
      dark: 'rgba(228, 228, 231, 0.60)',
      light: 'rgba(228, 228, 231, 0.56)',
      contrastText: '#000000'
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '14px',
          letterSpacing: '0.4px'
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          }
        },
        containedPrimary: {
          '&.Mui-disabled': {
            color: 'rgba(228, 228, 231, 0.3)',
            backgroundColor: 'rgba(228, 228, 231, 0.08)'
          }
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily,
          color: 'inherit'
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            fontFamily
          },
          '& .MuiInputLabel-root': {
            fontFamily
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily,
          fontSize: '16px'
        },
        input: {
          fontFamily,
          fontSize: '16px',
          '&::placeholder': {
            fontFamily
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e26', // Updated to match paper background
          color: '#e4e4e7', // Updated to match primary text
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.3)',
          '& *': {
            fontFamily
          }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e26', // Updated to match paper background
          borderRight: '1px solid rgba(228, 228, 231, 0.08)', // Updated divider color
          color: '#e4e4e7', // Updated to match primary text
          '& *': {
            fontFamily
          }
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '56px !important',
          '& *': {
            fontFamily
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove default paper elevation gradient in dark mode
          '& *': {
            fontFamily
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily
        },
        label: {
          fontFamily
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily
        },
        secondary: {
          fontFamily
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '& *': {
            fontFamily
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily
        },
        message: {
          fontFamily
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily,
          fontSize: '14px'
        }
      }
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontFamily
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          fontFamily,
          backgroundColor: '#1e1e26', // Match paper background
          border: '1px solid rgba(228, 228, 231, 0.08)', // Match divider color
          color: '#e4e4e7', // Match primary text
          '& .MuiDataGrid-main': {
            backgroundColor: '#1e1e26'
          },
          '& .MuiDataGrid-overlay': {
            backgroundColor: 'rgba(30, 30, 38, 0.8)'
          },
          // Header styling
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#121218', // Match default background
            borderBottom: '1px solid rgba(228, 228, 231, 0.08)',
            color: '#e4e4e7',
            fontWeight: 600
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#121218',
            '&:focus': {
              outline: 'none'
            },
            '&:focus-within': {
              outline: 'none'
            }
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontFamily,
            fontWeight: 600,
            color: '#e4e4e7'
          },
          // Row styling
          '& .MuiDataGrid-row': {
            backgroundColor: '#1e1e26',
            borderBottom: '1px solid rgba(228, 228, 231, 0.05)',
            '&:hover': {
              backgroundColor: 'rgba(228, 228, 231, 0.05)'
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(77, 184, 204, 0.12)', // primary.main with opacity
              '&:hover': {
                backgroundColor: 'rgba(77, 184, 204, 0.16)'
              }
            }
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
            color: '#e4e4e7',
            fontFamily,
            '&:focus': {
              outline: 'none'
            },
            '&:focus-within': {
              outline: 'none'
            }
          },
          // Footer styling
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: '#121218', // Match default background
            borderTop: '1px solid rgba(228, 228, 231, 0.08)',
            color: '#e4e4e7'
          },
          '& .MuiTablePagination-root': {
            color: '#e4e4e7'
          },
          '& .MuiTablePagination-selectLabel': {
            color: 'rgba(228, 228, 231, 0.65)' // Match secondary text
          },
          '& .MuiTablePagination-displayedRows': {
            color: 'rgba(228, 228, 231, 0.65)' // Match secondary text
          },
          // Toolbar styling
          '& .MuiDataGrid-toolbarContainer': {
            backgroundColor: '#121218',
            borderBottom: '1px solid rgba(228, 228, 231, 0.08)',
            padding: '8px 16px',
            '& .MuiButton-root': {
              color: '#e4e4e7'
            }
          },
          // Menu styling
          '& .MuiDataGrid-menuIcon': {
            color: 'rgba(228, 228, 231, 0.65)'
          },
          '& .MuiDataGrid-sortIcon': {
            color: 'rgba(228, 228, 231, 0.65)'
          },
          '& .MuiDataGrid-filterIcon': {
            color: 'rgba(228, 228, 231, 0.65)'
          },
          // Scrollbar styling
          '& .MuiDataGrid-virtualScroller': {
            '&::-webkit-scrollbar': {
              width: '6px',
              height: '6px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: '#26262e'
              }
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#3a3a44',
              borderRadius: '16px',
              '&:hover': {
                backgroundColor: '#4a4a54'
              }
            }
          },
          // Checkbox styling
          '& .MuiCheckbox-root': {
            color: 'rgba(228, 228, 231, 0.65)',
            '&.Mui-checked': {
              color: '#4db8cc' // Match primary.main
            }
          },
          // Loading overlay
          '& .MuiDataGrid-loadingOverlay': {
            backgroundColor: 'rgba(30, 30, 38, 0.8)',
            color: '#e4e4e7'
          },
          // No rows overlay
          '& .MuiDataGrid-noRowsOverlay': {
            backgroundColor: 'rgba(30, 30, 38, 0.8)',
            color: 'rgba(228, 228, 231, 0.65)'
          }
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@global': {
          '*': {
            fontFamily
          },
          html: {
            fontFamily
          },
          body: {
            fontFamily
          }
        },
        body: {
          backgroundColor: '#121218', // Updated to match default background
          color: '#e4e4e7', // Updated to match primary text
          fontFamily,
          scrollbarColor: '#3a3a44 transparent', // More subtle scrollbar
          '&::-webkit-scrollbar': {
            backgroundColor: 'transparent',
            width: '6px',
            height: '6px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: '#26262e' // More subtle track hover
            }
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#3a3a44', // More subtle thumb
            borderRadius: '16px',
            border: '1px solid transparent',
            '&:hover': {
              backgroundColor: '#4a4a54' // More subtle thumb hover
            }
          },
          '&::-webkit-scrollbar-button': {
            display: 'none'
          }
        }
      }
    }
  }
};

export const createAppTheme = (mode: 'light' | 'dark') => {
  return createTheme(mode === 'light' ? lightTheme : darkTheme);
};

export const theme = createTheme(lightTheme);
